import type {
  AiExecutionAuthority,
  AiExecutionGraph,
  AiExecutionPlan,
  AiOperationalCollaborationSignal,
  AiOperationalRecommendation,
  AiOperationSimulation,
  AiOperationTargetRef,
  AiOperationalPhase,
  AiOperationalRiskLevel,
  AiRollbackPlan,
} from '../../types/aiOperational'
import { ExecutionAffectedRecords } from './ExecutionAffectedRecords'
import { ExecutionAuthorityPill } from './ExecutionAuthorityPill'
import { ExecutionGraphView } from './ExecutionGraphView'
import { ExecutionRiskBadge } from './ExecutionRiskBadge'
import { ExecutionRiskPanel } from './ExecutionRiskPanel'
import { ExecutionSimulationPanel } from './ExecutionSimulationPanel'
import { OperationalContinuityPanel } from './OperationalContinuityPanel'
import { OperationalPlanSummary } from './OperationalPlanSummary'
import { RollbackAvailabilityPanel } from './RollbackAvailabilityPanel'
import { RollbackAvailabilityIndicator } from './RollbackAvailabilityIndicator'

type ExecutionPreviewCardProps = {
  title?: string
  summary: string
  risk: AiOperationalRiskLevel | 'UNKNOWN'
  authority: AiExecutionAuthority
  phase?: AiOperationalPhase
  reviewLabel?: string
  visibilityLabel?: string
  rollback?: AiRollbackPlan | null
  affectedRecords?: AiOperationTargetRef[]
  executionPlan?: AiExecutionPlan | null
  executionGraph?: AiExecutionGraph | null
  simulation?: AiOperationSimulation | null
  recommendations?: AiOperationalRecommendation[]
  collaborationSignals?: AiOperationalCollaborationSignal[]
  className?: string
}

export const ExecutionPreviewCard = ({
  title = 'Execution preview',
  summary,
  risk,
  authority,
  phase = 'PREVIEWING',
  reviewLabel,
  visibilityLabel,
  rollback,
  affectedRecords = [],
  executionPlan,
  executionGraph,
  simulation,
  recommendations,
  collaborationSignals,
  className = '',
}: ExecutionPreviewCardProps) => {
  const resolvedSimulation = simulation ?? executionGraph?.simulation ?? null
  return (
    <section className={`aiExecutionPreviewCard ${className}`} aria-label={title}>
      <div className="aiExecutionPreviewHeader">
        <div>
          <span className="aiExecutionPreviewEyebrow">Line operation</span>
          <strong>{title}</strong>
        </div>
        <span className="aiExecutionPhasePill">{phase.toLowerCase()}</span>
      </div>
      <p className="aiExecutionPreviewSummary">{summary}</p>
      <div className="aiExecutionPreviewMeta">
        <ExecutionRiskBadge risk={risk} />
        <ExecutionAuthorityPill authority={authority} />
        {reviewLabel ? <span>{reviewLabel}</span> : null}
        {visibilityLabel ? <span>{visibilityLabel}</span> : null}
        <RollbackAvailabilityIndicator rollback={rollback} />
        <ExecutionAffectedRecords records={affectedRecords} />
      </div>
      {executionPlan ? <OperationalPlanSummary plan={executionPlan} /> : null}
      <OperationalContinuityPanel
        recommendations={recommendations ?? resolvedSimulation?.continuity?.recommendations}
        collaborationSignals={collaborationSignals ?? resolvedSimulation?.continuity?.collaborationSignals}
      />
      <ExecutionSimulationPanel simulation={resolvedSimulation} />
      <ExecutionRiskPanel risk={risk} simulation={resolvedSimulation} />
      <RollbackAvailabilityPanel rollback={rollback} graph={executionGraph} />
      <ExecutionGraphView graph={executionGraph} />
    </section>
  )
}
